import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';

export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </Link>

      <div className="flex items-center gap-3 mb-8">
        <FileText className="w-8 h-8 text-accent-blue" />
        <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
      </div>

      <div className="prose prose-invert max-w-none space-y-8">
        <p className="text-gray-400">
          <strong>Effective Date:</strong> January 1, 2025<br />
          <strong>Last Updated:</strong> January 26, 2025
        </p>

        <section>
          <h2 className="text-xl font-semibold text-white">1. Acceptance of Terms</h2>
          <p className="text-gray-300">
            By accessing or using Northcoast AI Academy ("the Service"), you agree to be bound by these Terms of Service
            and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from
            using or accessing this site.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">2. Description of Service</h2>
          <p className="text-gray-300">
            Northcoast AI Academy provides online educational content focused on critical thinking, AI literacy,
            and practical AI skills. The Service includes courses, lessons, quizzes, AI-assisted learning features,
            and community resources.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">3. User Accounts</h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>You must be at least 13 years of age to use this Service</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials</li>
            <li>You agree to provide accurate, current, and complete information during registration</li>
            <li>You are responsible for all activities that occur under your account</li>
            <li>You must notify us immediately of any unauthorized use of your account</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">4. Subscription and Payment</h2>
          <p className="text-gray-300">
            Certain features of the Service require a paid subscription. By purchasing a subscription:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>You agree to pay all fees associated with your chosen plan</li>
            <li>Subscription fees are billed in advance on a recurring basis (monthly or annually)</li>
            <li>You authorize us to charge your payment method automatically for renewal periods</li>
            <li>All fees are non-refundable except as provided in our Refund Policy</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">5. Intellectual Property</h2>
          <p className="text-gray-300">
            The Service and its original content, features, and functionality are owned by Northcoast AI Academy
            and are protected by international copyright, trademark, and other intellectual property laws.
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>You may not copy, modify, distribute, sell, or lease any part of our Service</li>
            <li>You may not reverse engineer or attempt to extract the source code of our software</li>
            <li>Course materials are for personal, non-commercial use only</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">6. User Content</h2>
          <p className="text-gray-300">
            When you submit content to the Service (including reflections, comments, and AI interactions):
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>You retain ownership of your content</li>
            <li>You grant us a license to use, store, and display your content as necessary to provide the Service</li>
            <li>You represent that your content does not violate any third-party rights</li>
            <li>We may remove content that violates these terms</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">7. AI Features</h2>
          <p className="text-gray-300">
            Our Service includes AI-powered features. By using these features, you acknowledge that:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>AI responses are generated by third-party AI systems and may not always be accurate</li>
            <li>You should verify important information independently</li>
            <li>AI interactions may be logged for quality improvement purposes</li>
            <li>AI features are subject to rate limits and fair use policies</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">8. Prohibited Uses</h2>
          <p className="text-gray-300">You agree not to:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Use the Service for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to any part of the Service</li>
            <li>Share your account credentials with others</li>
            <li>Use automated systems to access the Service (bots, scrapers, etc.)</li>
            <li>Interfere with or disrupt the Service or its servers</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Upload malicious code or attempt to compromise security</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">9. Termination</h2>
          <p className="text-gray-300">
            We may terminate or suspend your account and access to the Service immediately, without prior notice,
            for any reason, including breach of these Terms. Upon termination, your right to use the Service will
            immediately cease.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">10. Disclaimer of Warranties</h2>
          <p className="text-gray-300">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
            WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">11. Limitation of Liability</h2>
          <p className="text-gray-300">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, NORTHCOAST AI ACADEMY SHALL NOT BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE
            OF THE SERVICE.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">12. Changes to Terms</h2>
          <p className="text-gray-300">
            We reserve the right to modify these terms at any time. We will notify users of significant
            changes via email or through the Service. Continued use of the Service after changes constitute
            acceptance of the new terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">13. Contact Information</h2>
          <p className="text-gray-300">
            For questions about these Terms of Service, please contact us at:
          </p>
          <p className="text-gray-300 mt-2">
            <strong>Email:</strong> legal@northcoast.ai<br />
            <strong>Address:</strong> Northcoast AI Academy, Cleveland, OH, USA
          </p>
        </section>
      </div>
    </div>
  );
}
